import { useState, useEffect, useCallback, useRef } from "react";
import { Users as UsersIcon, Briefcase, Search, X } from "lucide-react";
import toast from "react-hot-toast";
import { getEmployees, getPaymentPendingUsers, getRegisteredUsers } from "@/services/users";
import { getBranchEmployees } from "@/services/branchIncharge";
import { getZoneEmployees, getZoneRegisteredProfiles } from "@/services/zoneManager";
import EmployeeList from "./EmployeeList";
import UserList from "./UserList";
import { getMasterDropdownService } from "@/services/masters";
import { useSelector } from "react-redux";
import { RootState } from "@/lib/store";
import { useRoleContext } from "@/hooks/useRoleContext";
import PaymentPendingUserTable from "./PendingUserList";

export default function UserManagement() {
  const { loaded: permsLoaded, data: permissions } = useSelector((state: RootState) => state.permissions);
  const { isBranchIncharge, isZoneManager, branchCode, zoneCode } = useRoleContext();

  const canSeeEmployees = !permsLoaded || ("employees" in permissions && permissions["employees"]?.Can_View !== false);
  const canSeePaymentPendingUsers = !permsLoaded || ("pending payments" in permissions && permissions["pending payments"]?.Can_View !== false);

  // Branch Incharge only sees employees (their own branch), not registered users
  const canSeeUsers = !permsLoaded || ("resgistered profile" in permissions && permissions["resgistered profile"]?.Can_View !== false);
  const defaultTab = canSeeEmployees ? 'employees' : canSeeUsers ? 'users' : 'pending payments';
  const [activeTab, setActiveTab] = useState<'employees' | 'users' | 'pending payments'>(defaultTab);
  const skipRoleEffect = useRef(true);
  const [employees, setEmployees] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("True");
  const [roles, setRoles] = useState([]);

  // Employee pagination state (server-side)
  const [empPage, setEmpPage] = useState(1);
  const [empTotalPages, setEmpTotalPages] = useState(1);
  const [empTotalCount, setEmpTotalCount] = useState(0);
  const [paymentPendingUsers, setPaymentPendingUsers] =
    useState<any[]>([]);

  const [
    paymentPendingUsersTotalPages,
    setPaymentPendingUsersTotalPages,
  ] = useState(1);

  const [
    paymentPendingUsersTotalCount,
    setPaymentPendingUsersTotalCount,
  ] = useState(0);

  const [
    paymentPendingUsersPage,
    setPaymentPendingUsersPage,
  ] = useState(1);

  // Users pagination state (server-side)
  const [usersPage, setUsersPage] = useState(1);
  const [usersTotalPages, setUsersTotalPages] = useState(1);
  const [usersTotalCount, setUsersTotalCount] = useState(0);


  const loadEmployees = useCallback(async (role: string = '', page: number = 1, search: string = "", status: string = "") => {
    setLoading(true);
    try {
      const res = isZoneManager && zoneCode
        ? await getZoneEmployees(zoneCode, page, search || undefined, status)
        : isBranchIncharge && branchCode
          ? await getBranchEmployees(branchCode, page, search || undefined, status)
          : await getEmployees(role !== '' ? role : undefined, page, search || undefined, status);
      const d = res.data as any;
      if (d?.results) {
        setEmployees(d.results);
        setEmpTotalPages(d.total_pages ?? 1);
        setEmpTotalCount(d.count ?? 0);
      } else {
        // toast.error("Failed to load employees");
      }
    } catch (error) {
      toast.error("Error loading employees");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadUsers = useCallback(async (page: number = 1, search: string = "", status: string = "") => {
    if (canSeeUsers || isBranchIncharge) {
      setLoading(true);
      try {
        const { data } = isZoneManager && zoneCode
          ? await getZoneRegisteredProfiles(zoneCode, page, search || undefined, status)

          : await getRegisteredUsers(page, search || undefined, isBranchIncharge && branchCode ? branchCode : undefined, status);
        const d = data as any;
        if (d) {
          setUsers(d.results ?? d);
          setUsersTotalPages(d.total_pages ?? 1);
          setUsersTotalCount(d.count ?? (d.results?.length ?? 0));
        } else {
          // toast.error("Failed to load users");
        }
      } catch (error) {
        toast.error("Error loading users");
      } finally {
        setLoading(false);
      }
    }
  }, []);

  const loadPaymentPendingUsers = useCallback(
    async (
      page: number = 1,
      search: string = ""
    ) => {
      if (!canSeePaymentPendingUsers)
        return;

      setLoading(true);

      try {
        const response =
          await getPaymentPendingUsers();

        const d =
          response?.data;

        if (
          Array.isArray(d)
        ) {
          setPaymentPendingUsers(
            d
          );

          setPaymentPendingUsersTotalCount(
            d.length
          );

          setPaymentPendingUsersTotalPages(
            1
          );
        }

        // // if paginated response
        // else {
        //   setPaymentPendingUsers(d);

        //   setPaymentPendingUsersTotalCount(
        //     d?.count ??
        //       d?.results
        //         ?.length ??
        //       0
        //   );

        //   setPaymentPendingUsersTotalPages(
        //     d?.total_pages ??
        //       1
        //   );
        // }
      } catch (
      error
      ) {
        toast.error(
          "Error loading pending users"
        );
      } finally {
        setLoading(
          false
        );
      }
    },
    [
      canSeePaymentPendingUsers,
    ]
  );

  const loadRoles = useCallback(async () => {
    try {
      const response = await getMasterDropdownService();
      if (response.status === 200 && response.data) {
        const data = response.data as any;
        setRoles(data.Designations || []);
      }
    } catch (error) {
      // silent fail — filters still work without roles
    }
  }, []);

  // Initial load once on mount
  useEffect(() => {
    loadPaymentPendingUsers()
    loadEmployees(roleFilter, 1, "", '');
    loadUsers(1, "", '');
    loadRoles();
  }, []);

  // Debounced search / tab switch — skip on initial mount
  useEffect(() => {
    if (!searchQuery && activeTab === defaultTab) return; // skip initial state
    const id = setTimeout(() => {
      if (activeTab === 'employees') {
        setEmpPage(empPage || 1);
        loadEmployees(roleFilter, empPage || 1, searchQuery, statusFilter);
      } else if (
        activeTab ===
        "users"
      ) {
        setUsersPage(1);

        loadUsers(
          usersPage || 1,
          searchQuery, statusFilter
        );
      }

      else {
        setPaymentPendingUsersPage(
          paymentPendingUsersPage || 1
        );

        loadPaymentPendingUsers(
          paymentPendingUsersPage || 1,
          searchQuery
        );
      }
    }, searchQuery ? 500 : 0);
    return () => clearTimeout(id);
  }, [searchQuery, activeTab, statusFilter]);

  // roleFilter change — skip first render
  useEffect(() => {
    if (skipRoleEffect.current) { skipRoleEffect.current = false; return; }
    setEmpPage(1);
    loadEmployees(roleFilter, 1, searchQuery, statusFilter);
  }, [roleFilter, statusFilter]);

  const handleEmpPageChange = (page: number) => {
    setEmpPage(page);
    loadEmployees(roleFilter, page, searchQuery, statusFilter);
  };

  const handleUsersPageChange = (page: number) => {
    setUsersPage(page);
    loadUsers(page, searchQuery, statusFilter);
  };

  const clearFilters = () => {
    setSearchQuery("");
    setRoleFilter("");
    setStatusFilter("");
  };

  const handlePendingPageChange =
    (
      page: number
    ) => {
      setPaymentPendingUsersPage(
        page
      );

      loadPaymentPendingUsers(
        page,
        searchQuery
      );
    };
  const statusFilterOptions = [
    { value: "True", label: "Active" },
    { value: "False", label: "Inactive" },
  ]
  return (
    <div>
      <div>
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-extrabold text-gray-900">User & Team Management</h1>
          <p className="text-gray-600 mt-1">Manage employees, verify users, and control access</p>
        </div>

        {/* Tab Navigation */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 items-center justify-center">
          {canSeeEmployees && (
            <button
              onClick={() => setActiveTab('employees')}
              className={`flex flex-row items-center justify-center gap-4 px-6 py-4 rounded-lg font-semibold transition-all ${activeTab === 'employees'
                ? 'bg-brand-500 text-white shadow-lg'
                : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                }`}
            >
              <Briefcase className="w-5 h-5" />
              <span>Staff</span>
              <span
                className={`px-2 py-0.5 rounded-full text-xs font-bold ${activeTab === 'employees'
                  ? 'bg-brand-500 text-white'
                  : 'bg-brand-100 text-brand-700'
                  }`}
              >
                {empTotalCount}
              </span>
            </button>
          )}
          {canSeeUsers && (
            <button
              onClick={() => setActiveTab('users')}
              className={`flex flex-row items-center justify-center gap-4 px-6 py-4 rounded-lg font-semibold transition-all ${activeTab === 'users'
                ? 'bg-brand-500 text-white shadow-lg'
                : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                }`}
            >
              <UsersIcon className="w-5 h-5" />
              <span>Users</span>
              <span
                className={`px-2 py-0.5 rounded-full text-xs font-bold ${activeTab === 'users'
                  ? 'bg-brand-500 text-white'
                  : 'bg-green-100 text-green-700'
                  }`}
              >
                {usersTotalCount}
              </span>
            </button>
          )}
          {canSeePaymentPendingUsers && (
            <button
              onClick={() =>
                setActiveTab(
                  "pending payments"
                )
              }
              className={`flex flex-row items-center justify-center gap-4 px-6 py-4 rounded-lg font-semibold transition-all ${activeTab ===
                "pending payments"
                ? "bg-brand-500 text-white shadow-lg"
                : "bg-white text-gray-700 hover:bg-gray-100 border"
                }`}
            >

              <UsersIcon className="w-5 h-5" />

              <span>
                Pending Users
              </span>

              <span
                className={`px-2 py-0.5 rounded-full text-xs font-bold ${activeTab ===
                    "pending payments"
                    ? "bg-brand-500 text-white"
                    : "bg-red-100 text-red-700"
                  }`}
              >
                {
                  paymentPendingUsers?.length ||
                  paymentPendingUsersTotalCount ||
                  0
                }
              </span>

            </button>
          )}
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-700">Filters</h3>
            {(searchQuery || roleFilter !== "" || statusFilter !== "") && (
              <button
                onClick={clearFilters}
                className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-4 h-4" />
                Clear Filters
              </button>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            {/* Role Filter - only for employees tab */}
            {activeTab === 'employees' && (
              <select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400"
              >
                <option value="">All Roles</option>
                {roles.map((role: any) => (
                  <option key={role.id} value={role.Name}>{role.Name}</option>
                ))}
              </select>
            )}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400"
            >
              <option value="" disabled>
                Select Status
              </option>
              {statusFilterOptions.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Content */}
        {
          activeTab ===
            "employees"
            ? (
              <EmployeeList
                employees={employees}
                loading={loading}
                onRefresh={() =>
                  loadEmployees(
                    roleFilter,
                    empPage,
                    searchQuery, statusFilter
                  )
                }
                searchQuery={searchQuery}
                statusFilter={statusFilter}
                currentPage={empPage}
                totalPages={empTotalPages}
                totalCount={empTotalCount}
                onPageChange={
                  handleEmpPageChange
                }
                isBranchIncharge={
                  isBranchIncharge
                }
                branchCode={
                  branchCode
                }
              />
            )

            : activeTab ===
              "users"
              ? (
                <UserList
                  users={users}
                  loading={loading}
                  onRefresh={() =>
                    loadUsers(
                      usersPage,
                      searchQuery, statusFilter
                    )
                  }
                  searchQuery={searchQuery}
                  statusFilter={statusFilter}
                  currentPage={usersPage}
                  totalPages={
                    usersTotalPages
                  }
                  totalCount={
                    usersTotalCount
                  }
                  onPageChange={
                    handleUsersPageChange
                  }
                />
              )

              : (
                <PaymentPendingUserTable
                  data={
                    paymentPendingUsers
                  }
                />
              )
        }
      </div>
    </div>
  );
}
